<template>
    <div class="survey-receive-options">
        <div v-if="!hasAnswer">
            <button
                v-for="option in props.item.options"
                :key="option.key"
                class="survey-receive-options--item"
                type="button"
                @click="onOptionClick(option)"
            >
                {{ option.value }}
            </button>
        </div>
        <div v-else class="survey-receive-options--answer">
            <span>{{ selectedAnswer }}</span>
        </div>

        <!-- Move this outside the conditional rendering -->
        <div
            v-if="showCalendly"
            :id="calendlyFrameId"
            class="calendly-frame"
        ></div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, nextTick } from 'vue'
import {
    ConversationSpec,
    ReceiveOption,
    SurveyDisplayValues,
} from '@/entities/conversation'
import {
    initInlineWidget,
    hasCalendly,
    isCalendlyEvent,
} from '@/utils/calendly'

interface Props {
    item: ReceiveOption
    surveySpec: ConversationSpec
    surveyDisplayValues?: SurveyDisplayValues
}

const props = defineProps<Props>()
const emit = defineEmits(['callNext'])

const hasAnswer = ref(false)
const selectedAnswer = ref('')
const showCalendly = ref(false)
const calendlyFrameId = ref('')
const currentCalendlyFrame = ref<HTMLElement | null>(null)

const calendlyEventWindowSizes = {
    event_type_viewed: '970px',
    date_and_time_selected: '620px',
    event_scheduled: '490px',
}

onMounted(() => {
    if (
        props.surveyDisplayValues &&
        props.surveyDisplayValues[props.item?.param]
    ) {
        const selectedOptionsKey = props.surveyDisplayValues[props.item?.param]
        const selectedOption = props.item.options.find(
            (option) => option.key === selectedOptionsKey
        )

        if (selectedOption) {
            hasAnswer.value = true
            selectedAnswer.value = selectedOption.value
        }
    }
})

const onOptionClick = (option) => {
    if (option.key === 'yes' && props.item.link) {
        showCalendly.value = true
        calendlyFrameId.value = `calendly-frame-${props.item.param}`
        hasAnswer.value = true
        selectedAnswer.value = option.value
        nextTick(() => {
            loadCalendlyFrame(props.item.link, calendlyFrameId.value)
        })
    } else {
        hasAnswer.value = true
        selectedAnswer.value = option.value
        const newValue = { [props.item.param]: option.key }
        emit('callNext', newValue)
    }
}

const loadCalendlyFrame = (link: string | undefined, elementId: string) => {
    if (!hasCalendly(props.surveySpec)) {
        return
    }

    const calendlyFrame = document.getElementById(elementId)

    if (calendlyFrame) {
        initInlineWidget(link + '?hide_event_type_details=1', elementId)
        currentCalendlyFrame.value = calendlyFrame
        handleCalendlyMessages()
    }
}

const handleCalendlyMessages = () => {
    window.addEventListener('message', (e) => {
        if (isCalendlyEvent(e)) {
            if (currentCalendlyFrame.value) {
                currentCalendlyFrame.value.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                })
                const event = e.data.event.substring(9)
                if (calendlyEventWindowSizes[event] !== undefined) {
                    currentCalendlyFrame.value.style.height =
                        calendlyEventWindowSizes[event]
                }
                if (e.data.event === 'calendly.event_scheduled') {
                    emit('callNext')
                }
            }
        }
    })
}
</script>

<style lang="less">
.survey-receive-options {
    display: flex;
    flex-direction: column;
    align-items: center;
    > div {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &--item {
        color: var(--brandColor);
        background-color: #fff;
        border: 1px solid var(--brandColor);
        font-size: 1em;
        margin-bottom: 10px;
        padding: 12px 20px;
        text-align: center;
        width: 280px;
        cursor: pointer;
        transition: 0.25s;

        &:hover {
            color: #fff;
            background-color: var(--brandColor);
        }
    }
}

.calendly-frame {
    height: 480px;
    width: 100%;
    margin-top: 20px;
}
</style>
